import { Alert, Box, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { BackButton } from '../../../components/BackButton';
import { Loader } from '../../../components/Loader/Loader';
import { ROUTES } from '../../../constants/RouteNameMapping';
import { ICompanyDataModel } from '../../../data-models/company.data-model';
import { IManagedUserAccessDataModel } from '../../../data-models/managed-user-access.data-model';
import { IUserDataModel } from '../../../data-models/user.data-model';
import {
  getManagedUserList,
  getManagedUsersAccess,
} from '../../../services/queries/MaggieManagedUserQueries';
import { portfolioCompaniesAtom } from '../../../services/state/PortCosStateJ';
import { MaggieFeatureFlags } from '../../../util/feature-flags';
import { useAsync } from '../../../util/hook-utils';
import { CompanyView } from './CompanyView';
import { PeopleView } from './PeopleView';
import { useManageAccessViewModel } from './useManageAccess.view-model';

export function ManageAccessCompanyDataLoader() {
  const companies = useAtomValue(portfolioCompaniesAtom);
  const {
    data: accessList,
    error: managedAccErr,
    loading: managedAccLoading,
  } = useAsync(getManagedUsersAccess);
  const { data: userList, error: managedUserErr, loading: managedUserLoading } = useAsync(getManagedUserList);

  if (managedAccErr || managedUserErr) {
    return <Alert severity={'error'}>Failed to load access data</Alert>;
  }

  if (managedAccLoading === 'loading' || managedUserLoading === 'loading') {
    return <Loader />;
  }

  return (
    <ManageAccessCompany
      portfolioCompanies={companies}
      userList={userList ?? []}
      userAccess={accessList ?? []}
    />
  );
}

export interface ManageAccessCompanyProps {
  portfolioCompanies: ICompanyDataModel[];
  userList: IUserDataModel[];
  userAccess: IManagedUserAccessDataModel[];
}

export function ManageAccessCompany(props: ManageAccessCompanyProps) {
  const { portfolioCompanies, userList, userAccess } = props;
  const { showAdminManageAccessColumnGridDisplay } = useFlags<MaggieFeatureFlags>();
  const {
    getAccessForCompany,
    getAccessForUser,
    handleUserSingleSelect,
    handleUserMultiSelect,
    handleCompanySingleSelect,
    handleCompanyMultiSelect,
    getAggregatedAccessStateForUser,
    getAggregatedAccessStateForCompany,
    handleBulkAdd,
    handleBulkRemove,
    handleSelectAllCompanies,
    handleSelectAllUsers,
    handleDeselectAllCompanies,
    handleDeselectAllUsers,
    groupByCompany,
    selectedCompanies,
    selectedUsers,
    onGroupChanged,
    sortedCompanies,
    sortedUsers,
    companyFilter,
    setCompanyFilter,
    userFilter,
    setUserFilter,
    filteredCompanies,
    filteredUsers,
  } = useManageAccessViewModel(portfolioCompanies, userAccess, userList);

  const view = groupByCompany ? (
    <CompanyView
      companies={sortedCompanies}
      filteredCompanies={filteredCompanies}
      companyFilter={companyFilter}
      onCompanyFilterChange={setCompanyFilter}
      userList={sortedUsers}
      selectedCompanies={selectedCompanies}
      onCompanySelection={handleCompanySingleSelect}
      onCompanyCheckboxToggle={handleCompanyMultiSelect}
      onSelectAll={handleSelectAllCompanies}
      onDeselectAll={handleDeselectAllCompanies}
      onBulkAdd={handleBulkAdd}
      onBulkRemove={handleBulkRemove}
      getAccessForCompany={getAccessForCompany}
      getAggregatedAccessStateForUser={getAggregatedAccessStateForUser}
    />
  ) : (
    <PeopleView
      companies={sortedCompanies}
      userList={sortedUsers}
      filteredUsers={filteredUsers}
      userFilter={userFilter}
      onUserFilterChange={setUserFilter}
      selectedUsers={selectedUsers}
      onUserCheckboxToggle={handleUserMultiSelect}
      onUserSelection={handleUserSingleSelect}
      onSelectAll={handleSelectAllUsers}
      onDeselectAll={handleDeselectAllUsers}
      onBulkAdd={handleBulkAdd}
      onBulkRemove={handleBulkRemove}
      getAccessForUser={getAccessForUser}
      getAggregatedAccessStateForCompany={getAggregatedAccessStateForCompany}
    />
  );

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: '8px 12px 12px',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      <Stack direction='row' gap='0.5rem' alignItems='center' marginBottom='1rem'>
        {showAdminManageAccessColumnGridDisplay && (
          <BackButton
            to={`/${ROUTES.ADMIN_PANEL}/${ROUTES.ADMIN_PANEL_MANAGE_ACCESS}`}
            title='Go back to Admin Panel'
          />
        )}
        <Typography variant='h4' component='h1'>
          Manage Access
        </Typography>
      </Stack>

      <ToggleButtonGroup
        color={'secondary'}
        exclusive
        onChange={(_, value) => {
          if (value) {
            onGroupChanged(value);
          }
        }}
        sx={{ width: '100%', marginBottom: '1rem' }}
        value={groupByCompany ? 'company' : 'email'}
      >
        <ToggleButton value='company' sx={{ flex: 1 }}>
          By Company
        </ToggleButton>
        <ToggleButton value='email' sx={{ flex: 1 }}>
          By People
        </ToggleButton>
      </ToggleButtonGroup>

      <Box sx={{ flex: 1, overflow: 'hidden' }}>{view}</Box>
    </Box>
  );
}
